import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { MobileOptimizer } from "./components/performance/MobileOptimizer";
import { CriticalCSSLoader } from "./components/performance/CriticalCSS";
import { ResourcePriorityOptimizer } from "./components/performance/ResourcePriority";
import { CriticalImagePreloader } from "./components/performance/CriticalImagePreloader";
import { ResourceHints } from "./components/performance/ResourceHints";

// Register mobile service worker for aggressive caching
if ('serviceWorker' in navigator && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/mobile-sw.js')
      .then(registration => console.log('Mobile SW registered:', registration.scope))
      .catch(err => console.log('Mobile SW registration failed:', err));
  });
}

// Performance monitoring with Core Web Vitals
if (import.meta.env.DEV) {
  // Log performance metrics after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      const resources = performance.getEntriesByType('resource');
      
      console.log('🚀 Performance Metrics:', {
        'DOM Content Loaded': `${(perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart).toFixed(2)}ms`,
        'Page Load Time': `${(perfData.loadEventEnd - perfData.loadEventStart).toFixed(2)}ms`,
        'Total Resources': resources.length,
        'DNS Lookup': `${(perfData.domainLookupEnd - perfData.domainLookupStart).toFixed(2)}ms`,
        'TCP Connection': `${(perfData.connectEnd - perfData.connectStart).toFixed(2)}ms`,
        'Time to First Byte': `${(perfData.responseStart - perfData.requestStart).toFixed(2)}ms`,
      });
      
      // Core Web Vitals tracking
      if ('PerformanceObserver' in window) {
        // Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          console.log('📊 LCP (Largest Contentful Paint):', `${lastEntry.renderTime || lastEntry.loadTime}ms`);
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            console.log('⚡ FID (First Input Delay):', `${entry.processingStart - entry.startTime}ms`);
          });
        });
        fidObserver.observe({ type: 'first-input', buffered: true });

        // Cumulative Layout Shift (CLS)
        let clsScore = 0;
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsScore += (entry as any).value;
            }
          }
          console.log('📐 CLS (Cumulative Layout Shift):', clsScore.toFixed(4));
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });
      }

      // Memory usage (if available)
      if ((performance as any).memory) {
        const memory = (performance as any).memory;
        console.log('💾 Memory Usage:', {
          'Used': `${(memory.usedJSHeapSize / 1048576).toFixed(2)} MB`,
          'Total': `${(memory.totalJSHeapSize / 1048576).toFixed(2)} MB`,
          'Limit': `${(memory.jsHeapSizeLimit / 1048576).toFixed(2)} MB`,
        });
      }
    }, 0);
  });
}

createRoot(document.getElementById("root")!).render(
  <>
    <ResourceHints />
    <MobileOptimizer />
    <CriticalCSSLoader />
    <ResourcePriorityOptimizer />
    <CriticalImagePreloader />
    <App />
  </>
);
